import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  LABELING_STRATEGIES_REQUIRING_NAME,
  RegistryLabelingStrategy,
  TagSchema,
} from 'constants/RegistryConstants';
import {ClientId} from 'notebook/ELNConstants';
import {
  defaultStructuredTableColumnsForRegisterBioentity,
  filterOutNonDefaultColumns,
  getBioentityPrimarySamplesFromStructuredTable,
  getTagSchemaForStructuredTable,
} from 'notebook/integrations/registry/StructuredTableUtilsForRegistration';
import {ColumnDefinition, UNITS_COLUMN_NAME} from 'notebook/structured-tables/columns/ColumnDefinitions';
import isMissingFieldColumn from 'notebook/structured-tables/columns/isMissingFieldColumn';
import isReadOnly from 'notebook/structured-tables/columns/isReadOnly';
import {
  isMeasurementAmountStructuredTableColumn,
  isTagValueStructuredTableColumn,
  isUnitSelectorStructuredTableColumn,
} from 'notebook/structured-tables/columns/TypePredicates';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import MixtureStructuredTableRowUpdater from 'notebook/structured-tables/row-updaters/MixtureStructuredTableRowUpdater';
import isMixtureTableMappingEnabled from 'notebook/structured-tables/structured-table-mappings/isMixtureTableMappingEnabled';
import {
  BaseStructuredTable,
  EvalRowView,
  PrimarySample,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {fieldsPerForRowStructuredTableFormLayout} from 'notebook/structured-tables/StructuredTableForm';
import {getTypedValueFromCell, getTypedValueFromTable} from 'notebook/structured-tables/util/getTypedValue';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {RowMetadata} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {Table, TableReorderLocks} from 'notebook/tables/constants/TableConstants';
import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {
  coerceToPrimitive,
  getBioentityInfoFromTypedValue,
  getIdsFromTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  DEFAULT_MIXTURE_AMOUNT,
  DEFAULT_MIXTURE_UNITS,
  ORDERED_MIXTURE_UNITS_FOR_DISPLAY,
} from 'registry/mixtures/MixtureConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';

const AMOUNT_COLUMN_NAME = 'Target Amount';

export function _isParentLinkMixtureFieldColumn(
  column: StructuredTableColumn,
  parentLinkMixtureFieldId: string | null
): boolean {
  if (parentLinkMixtureFieldId == null) {
    return false;
  }
  return isTagValueStructuredTableColumn(column) && column.schemaFieldApiId === parentLinkMixtureFieldId;
}

export function getMixtureAmountColumnIndex(table: MixtureMetadataStructuredTable): number {
  const columnIndex = table.columns.findIndex(
    (column) => column.columnType === TableColumnType.MEASUREMENT_AMOUNT
  );
  assert(
    columnIndex !== -1,
    'Expected MixtureMetadataStructuredTable columns to include a column for the mixture amount'
  );
  return columnIndex;
}

function getMixtureAmountValueLegacy(table: MixtureMetadataStructuredTable): TypedValue | null {
  assert(table.rows.length === 1, 'Expected MixtureMetadataStructuredTable to have exactly 1 row');
  return getTypedValueFromTable(table, 0, getMixtureAmountColumnIndex(table));
}

/**
 * Legacy reader used when ENABLE_MIXTURE_PREP_ENTRY_EVALUATION is off. The form-based renderer
 * only ever writes `typedValue`, so the strict `getTypedValueFromCell` invariant assertion in
 * `getMixtureAmountValueLegacy` holds. Will be deleted once the feature migration completes.
 */
function getMixtureAmountFromTableLegacy(table: MixtureMetadataStructuredTable): string {
  const typedValue = getMixtureAmountValueLegacy(table);
  if (typedValue == null) {
    return DEFAULT_MIXTURE_AMOUNT.stringValue;
  }
  assert(
    typedValue.type === BaseTypedValueType.STRING,
    `Expected typed value for mixture amount cell to be of type STRING, but was ${typedValue.type}`
  );
  return coerceToPrimitive(typedValue) as string;
}

/**
 * When ENABLE_MIXTURE_PREP_ENTRY_EVALUATION is on, the mixture metadata table is rendered as a
 * Single Input table and edits flow through the standard table cell editor, which persists both
 * `text` (the raw user input) and `typedValue` (the evaluated result) on the cell. This reader
 * is tolerant of `text`: if `text` is non-empty, return it directly. Otherwise fall back to the
 * typedValue, returning DEFAULT_MIXTURE_AMOUNT.stringValue only when the cell is fully empty
 * (both `text === ''` and `typedValue` is null/undefined).
 */
export function getMixtureAmountFromTable(table: MixtureMetadataStructuredTable): string {
  if (!BenchlingConfig.ENABLE_MIXTURE_PREP_ENTRY_EVALUATION) {
    return getMixtureAmountFromTableLegacy(table);
  }
  assert(table.rows.length === 1, 'Expected MixtureMetadataStructuredTable to have exactly 1 row');
  const cell = table.rows[0]?.cells[getMixtureAmountColumnIndex(table)];
  const text = cell?.text ?? '';
  if (text !== '') {
    return text;
  }
  const typedValue = cell?.typedValue ?? null;
  if (typedValue == null) {
    return DEFAULT_MIXTURE_AMOUNT.stringValue;
  }
  assert(
    typedValue.type === BaseTypedValueType.STRING,
    `Expected typed value for mixture amount cell to be of type STRING, but was ${typedValue.type}`
  );
  return coerceToPrimitive(typedValue) as string;
}

export function getParentMixtureColumnIndex(
  columns: ReadonlyArray<StructuredTableColumn>,
  tableMetadata: MixtureMetadataStructuredTableMetadata
): number {
  const columnIndex = columns.findIndex((column) =>
    _isParentLinkMixtureFieldColumn(column, tableMetadata.parentLinkMixtureFieldId)
  );
  return columnIndex;
}

function getParentMixtureIdFromTypedValue(typedValue: TypedValue | null | undefined): string | null {
  if (typedValue == null) {
    return null;
  }
  const ids = getIdsFromTypedValue(typedValue);
  if (ids == null) {
    // The typedValue might be a placeholder introduced via template migration, or some other value which we
    // should ignore in this case.
    return null;
  }
  if (ids.length === 0) {
    return null;
  } else if (ids.length === 1) {
    return ids[0];
  } else {
    assert(false, 'Expected parent link mixture field to be single-select');
  }
}

function getParentMixtureIdLegacy(
  mixtureRow: EvalRowView<MixtureMetadataStructuredTableRowMetadata>,
  columns: ReadonlyArray<StructuredTableColumn>,
  tableMetadata: MixtureMetadataStructuredTableMetadata
): string | null {
  const columnIndex = getParentMixtureColumnIndex(columns, tableMetadata);
  if (columnIndex === -1) {
    return null;
  }
  return getParentMixtureIdFromTypedValue(getTypedValueFromCell(mixtureRow.cells[columnIndex]));
}

/**
 * Sentinel returned from {@link getParentMixtureIdFromTable} when the parent mixture cell contains
 * input that cannot be resolved to a single valid mixture ID (for example, partial user text in the
 * Single Input cell, or a typedValue whose shape is not a single-link mixture reference). Callers
 * use this to distinguish "invalid / mid-edit input" from "explicitly cleared" (`null`) and from
 * "valid parent ID selected" (a `string`).
 *
 * Only returned when ENABLE_MIXTURE_PREP_ENTRY_EVALUATION is on. Under the legacy form-based
 * rendering invalid inputs were not possible, so this branch never fires.
 */
export const INVALID_PARENT_MIXTURE_ID: unique symbol = Symbol('INVALID_PARENT_MIXTURE_ID');

export type ParentMixtureIdFromTableResult = string | null | typeof INVALID_PARENT_MIXTURE_ID;

/**
 * Used when ENABLE_MIXTURE_PREP_ENTRY_EVALUATION is on. Unlike {@link getParentMixtureIdLegacy},
 * this reader does not assert that `cell.text` is empty (the Single Input cell editor persists
 * both `text` and `typedValue`), and it surfaces invalid inputs via
 * {@link INVALID_PARENT_MIXTURE_ID} instead of collapsing them to `null` (which the reducer
 * treats as "parent was cleared").
 */
function getParentMixtureId(
  mixtureRow: EvalRowView<MixtureMetadataStructuredTableRowMetadata>,
  columns: ReadonlyArray<StructuredTableColumn>,
  tableMetadata: MixtureMetadataStructuredTableMetadata
): ParentMixtureIdFromTableResult {
  const columnIndex = getParentMixtureColumnIndex(columns, tableMetadata);
  if (columnIndex === -1) {
    return null;
  }
  const cell = mixtureRow.cells[columnIndex];
  const text = cell?.text ?? '';
  const typedValue = cell?.typedValue ?? null;
  if (text === '' && typedValue == null) {
    return null;
  }
  if (typedValue != null) {
    const ids = getIdsFromTypedValue(typedValue);
    if (ids != null) {
      if (ids.length === 1) {
        return ids[0];
      }
      if (ids.length === 0 && text === '') {
        return null;
      }
    }
  }
  return INVALID_PARENT_MIXTURE_ID;
}

export function getParentMixtureIdFromTable(
  table: MixtureMetadataStructuredTable
): ParentMixtureIdFromTableResult {
  assert(table.rows.length === 1, 'Expected MixtureMetadataStructuredTable to have exactly 1 row');
  if (!BenchlingConfig.ENABLE_MIXTURE_PREP_ENTRY_EVALUATION) {
    return getParentMixtureIdLegacy(table.rows[0], table.columns, table.metadata);
  }
  return getParentMixtureId(table.rows[0], table.columns, table.metadata);
}

export function getParentMixtureIdFromTableTypedValueOnly(
  table: MixtureMetadataStructuredTable
): string | null {
  assert(table.rows.length === 1, 'Expected MixtureMetadataStructuredTable to have exactly 1 row');
  const columnIndex = getParentMixtureColumnIndex(table.columns, table.metadata);
  if (columnIndex === -1) {
    return null;
  }
  return getParentMixtureIdFromTypedValue(table.rows[0]?.cells[columnIndex]?.typedValue);
}

/**
 * Returns true if the parent-link mixture cell holds any value at all — either raw `text`
 * entered through the Single Input cell editor, or an evaluated `typedValue`. This is broader
 * than {@link getParentMixtureIdFromTable}, which also considers whether the value resolves to
 * a single mixture link; callers that just need to know whether the user has put *anything* in
 * the cell (e.g. for read-only gating on dependent columns) should use this helper instead.
 */
function isParentLinkMixtureCellNonEmpty(
  mixtureRow: EvalRowView<MixtureMetadataStructuredTableRowMetadata>,
  columns: ReadonlyArray<StructuredTableColumn>,
  tableMetadata: MixtureMetadataStructuredTableMetadata
): boolean {
  const columnIndex = getParentMixtureColumnIndex(columns, tableMetadata);
  if (columnIndex === -1) {
    return false;
  }
  const cell = mixtureRow.cells[columnIndex];
  if (cell == null) {
    return false;
  }
  return cell.text !== '' || cell.typedValue != null;
}

function getColumnForParentMixtureField(
  columns: Array<StructuredTableColumn>,
  parentLinkMixtureFieldId: string
): StructuredTableColumn | undefined {
  return columns.find((column) => _isParentLinkMixtureFieldColumn(column, parentLinkMixtureFieldId));
}

function orderParentMixtureFieldColumnFirst(
  baseColumns: Array<StructuredTableColumn>,
  parentLinkMixtureFieldId: string | null
): Array<StructuredTableColumn> {
  if (parentLinkMixtureFieldId == null) {
    return baseColumns;
  }
  const columnForParentMixtureField = getColumnForParentMixtureField(baseColumns, parentLinkMixtureFieldId);
  assert(
    columnForParentMixtureField != null,
    'Expected column for parent mixture field to exist if parentMixtureFieldId is present'
  );
  const [schemaFieldColumns, otherColumns] = _.partition(baseColumns, (column) =>
    isTagValueStructuredTableColumn(column)
  );
  return [
    ...otherColumns,
    columnForParentMixtureField,
    ...schemaFieldColumns.filter((column) => column !== columnForParentMixtureField),
  ];
}

export interface MixtureMetadataStructuredTableMetadata {
  labelingStrategy: RegistryLabelingStrategy;
  parentLinkMixtureFieldId: string | null;
}
export interface MixtureMetadataStructuredTableRowMetadata extends RowMetadata {}

function mixtureMetadataStructuredTableFormLayout(
  columns: Array<ColumnDefinition>,
  formFields: Array<JSX.Element>
): JSX.Element | null {
  const topHalfFormControls: Array<JSX.Element> = [];
  const bottomHalfFormControls: Array<JSX.Element> = [];
  const topHalfColumnDefinitions: Array<ColumnDefinition> = [];
  const bottomHalfColumnDefinitions: Array<ColumnDefinition> = [];
  formFields.forEach((formField, columnIndex) => {
    const columnDefinition = columns[columnIndex];
    if (
      columnDefinition.type === TableColumnType.MEASUREMENT_AMOUNT ||
      columnDefinition.type === TableColumnType.UNIT_SELECTOR
    ) {
      bottomHalfFormControls.push(formField);
      bottomHalfColumnDefinitions.push(columnDefinition);
    } else {
      topHalfFormControls.push(formField);
      topHalfColumnDefinitions.push(columnDefinition);
    }
  });
  return (
    <Row bufferRowReset={true}>
      {fieldsPerForRowStructuredTableFormLayout(topHalfColumnDefinitions, topHalfFormControls)}
      <Col className="clearfix" />
      <hr
        className={classNames(
          css(styles.hr),
          BenchlingConfig.TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE && 'notebook-export--mixture-metadata-hr'
        )}
      />
      {fieldsPerForRowStructuredTableFormLayout(bottomHalfColumnDefinitions, bottomHalfFormControls)}
    </Row>
  );
}

export default class MixtureMetadataStructuredTableAdapter extends GenericStructuredTableAdapter<
  MixtureMetadataStructuredTableMetadata,
  MixtureMetadataStructuredTableRowMetadata
> {
  getDisplayName(
    table: StructuredTable<MixtureMetadataStructuredTableMetadata, MixtureMetadataStructuredTableRowMetadata>
  ): string {
    return 'Create mixture preps';
  }

  override allowAsTableMappingDestination(
    _table: StructuredTable<MixtureMetadataStructuredTableMetadata, MixtureMetadataStructuredTableRowMetadata>
  ): boolean {
    return isMixtureTableMappingEnabled();
  }

  override shouldShowAsForm(
    table: StructuredTable<MixtureMetadataStructuredTableMetadata, MixtureMetadataStructuredTableRowMetadata>
  ): boolean {
    assert(table.rows.length === 1, 'Mixture metadata table must have exactly one row!');
    // When ENABLE_MIXTURE_PREP_ENTRY_EVALUATION is enabled, render the metadata table as a Single
    // Input table (see injectFrontEndMetadata) instead of as a free-form layout. Keep returning
    // true otherwise to preserve the existing form rendering.
    return !BenchlingConfig.ENABLE_MIXTURE_PREP_ENTRY_EVALUATION;
  }

  override shouldSuppressTitleBar(
    _table: StructuredTable<MixtureMetadataStructuredTableMetadata, MixtureMetadataStructuredTableRowMetadata>
  ): boolean {
    return BenchlingConfig.ENABLE_MIXTURE_PREP_ENTRY_EVALUATION;
  }

  /**
   * When ENABLE_MIXTURE_PREP_ENTRY_EVALUATION is enabled, all of the metadata table's columns
   * are migrated to Single Inputs (see `injectFrontEndMetadata`) so the table can preserve its
   * form-like appearance. Returning true here puts the underlying `TableEditable` into Single
   * Input-only view: the footer, table body, and surrounding border are hidden, and Single
   * Input columns can no longer be converted back to non-single-input columns.
   */
  override shouldRenderAsSingleInputView(
    _table: StructuredTable<MixtureMetadataStructuredTableMetadata, MixtureMetadataStructuredTableRowMetadata>
  ): boolean {
    return BenchlingConfig.ENABLE_MIXTURE_PREP_ENTRY_EVALUATION;
  }

  override renderFormContents(
    columns: Array<ColumnDefinition>,
    formFields: Array<JSX.Element>
  ): JSX.Element | null {
    return mixtureMetadataStructuredTableFormLayout(columns, formFields);
  }

  override renderFormField(
    editor: JSX.Element,
    columnDefinition: ColumnDefinition,
    columnIndex: number
  ): JSX.Element {
    if (columnDefinition.type === TableColumnType.MEASUREMENT_AMOUNT) {
      return (
        <Col
          sm={2}
          className={classNames(
            css(!editor.props.viewer && styles.measurementAmount),
            !editor.props.viewer &&
              BenchlingConfig.TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE &&
              'notebook-export--mixture-measurement-amount'
          )}
        >
          {editor}
        </Col>
      );
    }
    if (columnDefinition.type === TableColumnType.UNIT_SELECTOR) {
      return (
        <Col
          sm={2}
          className={classNames(
            css(!editor.props.viewer && styles.unitSelector),
            !editor.props.viewer &&
              BenchlingConfig.TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE &&
              'notebook-export--mixture-unit-selector'
          )}
        >
          {editor}
        </Col>
      );
    }
    return super.renderFormField(editor, columnDefinition, columnIndex);
  }

  getBlankRowMetadata(
    tableMetadata: MixtureMetadataStructuredTableMetadata
  ): MixtureMetadataStructuredTableRowMetadata {
    return {};
  }

  override isPositionReadOnly(
    currentColumnIndex: number,
    row: EvalRowView<MixtureMetadataStructuredTableRowMetadata>,
    structuredTable: MixtureMetadataStructuredTable
  ): boolean {
    const {columns, schemas, metadata} = structuredTable;
    const currentColumn = columns[currentColumnIndex];
    if (isReadOnly(currentColumn) || isMissingFieldColumn(currentColumn, schemas)) {
      return true;
    }
    if (
      _isParentLinkMixtureFieldColumn(currentColumn, metadata.parentLinkMixtureFieldId) &&
      row.isFinalized
    ) {
      return true;
    }
    switch (currentColumn.columnType) {
      case TableColumnType.ALIAS:
      case TableColumnType.BIOENTITY_NAME:
        return row.isFinalized;
      case TableColumnType.UNIT_SELECTOR:
        // Lock the Units column whenever the parent-link cell contains a value.
        return isParentLinkMixtureCellNonEmpty(row, columns, metadata);
      default:
        return false;
    }
  }

  override injectFrontEndMetadata({
    column,
    schemas,
    tableMetadata,
  }: {
    column: StructuredTableColumn;
    schemas: Array<SchemaAndColumnGroup>;
    tableMetadata: MixtureMetadataStructuredTableMetadata;
  }): StructuredTableColumn {
    if (isUnitSelectorStructuredTableColumn(column)) {
      return {
        ...column,
        frontEndMetadata: {acceptedUnits: ORDERED_MIXTURE_UNITS_FOR_DISPLAY},
      };
    } else if (isMeasurementAmountStructuredTableColumn(column)) {
      return {...column, frontEndMetadata: {allowQS: false}};
    }
    return column;
  }

  override _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: MixtureMetadataStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[p: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const baseColumns = orderParentMixtureFieldColumnFirst(
      super._getAllSupportedColumns({
        entryId,
        metadata,
        schemas,
        selectorsById,
        structuredTableInsertionContext,
      }),
      metadata.parentLinkMixtureFieldId
    );
    const amountColumn = makeStructuredTableColumn({
      columnType: TableColumnType.MEASUREMENT_AMOUNT,
      name: AMOUNT_COLUMN_NAME,
      isRequired: true,
      defaultValue: {
        typedValue: {
          type: BaseTypedValueType.STRING,
          rawValue: DEFAULT_MIXTURE_AMOUNT.stringValue,
        },
        text: '',
      },
    });
    const unitsColumn = makeStructuredTableColumn({
      columnType: TableColumnType.UNIT_SELECTOR,
      name: UNITS_COLUMN_NAME,
      isRequired: true,
      defaultValue: {
        typedValue: {type: BaseTypedValueType.STRING, rawValue: DEFAULT_MIXTURE_UNITS},
        text: '',
      },
    });
    return [
      // Note that while this includes an ALIAS column, it gets filtered out in getInitialColumns.
      ...defaultStructuredTableColumnsForRegisterBioentity({
        baseColumns,
        bioentityExists: false,
        entityNameColumnConfiguration: LABELING_STRATEGIES_REQUIRING_NAME.includes(metadata.labelingStrategy)
          ? 'REQUIRED'
          : 'ABSENT',
      }),
      amountColumn,
      unitsColumn,
    ];
  }

  override getInitialColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: MixtureMetadataStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[p: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    return filterOutNonDefaultColumns(
      super.getInitialColumns({
        entryId,
        metadata,
        schemas,
        selectorsById,
        structuredTableInsertionContext,
      })
    );
  }

  override toTable(
    structuredTable: StructuredTable<
      MixtureMetadataStructuredTableMetadata,
      MixtureMetadataStructuredTableRowMetadata
    >
  ): Table {
    return {
      ...super.toTable(structuredTable),
      allowAddRows: false,
      lockedDims: {allowAddRows: false, allowRemoveRows: false, allowEditCols: true},
    };
  }

  // Keep in sync with MixtureMetadataTableInstantiator.get_primary_sample_ids_for_row (which delegates to
  // get_bioentity_primary_sample_ids) in
  // benchling/notebook/structured_tables/instantiators/mixture_metadata_table_instantiator.py (tested by
  // TestMixtureMetadataInstantiatorPrimarySamples in
  // tests/unit/notebook/structured_tables/instantiators/mixture_metadata_table_instantiator_test.py)
  override getPrimarySamples(
    structuredTable: MixtureMetadataStructuredTable,
    computedTable?: Array<Array<EvaluatedCell>> | null
  ): {[rowId: string]: Array<PrimarySample>} {
    return getBioentityPrimarySamplesFromStructuredTable(structuredTable, computedTable);
  }

  getTagSchema(structuredTable: MixtureMetadataStructuredTable): TagSchema | null {
    return getTagSchemaForStructuredTable(structuredTable);
  }

  // TODO(BNCH-150881): Once ENABLE_MIXTURE_PREP_ENTRY_EVALUATION is permanent, evaluated table data is
  // always available, so make `computedTable` non-nullable and delete the
  // getTypedValueFromTable raw-cell fallback below (along with the import if it becomes unused).
  getMixtureId(
    table: MixtureMetadataStructuredTable,
    computedTable: Array<Array<EvaluatedCell>> | null
  ): string | null {
    assert(table.rows.length === 1, 'Expected MixtureMetadataStructuredTable to have exactly 1 row');
    const bioentityColumnIndex = table.columns.findIndex(
      ({columnType}) => columnType === TableColumnType.EXISTING_BIOENTITY
    );
    assert(
      bioentityColumnIndex !== -1,
      'Expected MixtureMetadataStructuredTable to have an EXISTING_BIOENTITY column'
    );
    const bioentityTypedValue =
      computedTable != null
        ? (computedTable[0]?.[bioentityColumnIndex]?.value ?? null)
        : getTypedValueFromTable(table, 0, bioentityColumnIndex);
    return bioentityTypedValue ? (getBioentityInfoFromTypedValue(bioentityTypedValue)?.id ?? null) : null;
  }

  override getTableKindRowUpdater(): typeof MixtureStructuredTableRowUpdater {
    return MixtureStructuredTableRowUpdater;
  }

  override allowReorderDimensions(
    structuredTable: StructuredTable<
      MixtureMetadataStructuredTableMetadata,
      MixtureMetadataStructuredTableRowMetadata
    >
  ): TableReorderLocks {
    return {
      [TableDimension.ROW]: false,
      [TableDimension.COLUMN]: false,
    };
  }
}

const styles = StyleSheet.create({
  hr: {
    marginLeft: '10px',
    marginRight: '10px',
  },
  measurementAmount: {
    paddingRight: 0,
  },
  unitSelector: {
    paddingLeft: 0,
  },
});

export type GenericMixtureMetadataStructuredTable<TIds> = BaseStructuredTable<
  MixtureMetadataStructuredTableMetadata,
  MixtureMetadataStructuredTableRowMetadata,
  TIds
>;
export type MixtureMetadataStructuredTable = GenericMixtureMetadataStructuredTable<ClientId>;
